import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Send, PlayCircle, PauseCircle, StopCircle, FileCheck, RefreshCw, Ban } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import { usePermissions } from '../context/PermissionContext';
import FileUploadWidget from './FileUploadWidget';
// PTW-APPROVER-LOCATION: site acknowledgement + approver location capture is
// disabled until the nginx Permissions-Policy change (geolocation=(self)) is
// validated by the dev team. Re-enable every PTW-APPROVER-LOCATION block in
// this file together with the ones in backend/routes/ptw.py.
// import { captureLocation } from '../utils/geolocation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWWorkflowActions = ({ permit, onUpdate }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const { hasPermission, hasRole, user } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [modalData, setModalData] = useState({});
  const [error, setError] = useState(null);
  const [closeFiles, setCloseFiles] = useState([]);
  // PTW-APPROVER-LOCATION (disabled):
  // const [capturingLocation, setCapturingLocation] = useState(false);

  const handleAction = async (action, payload = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      let response;
      
      switch (action) {
        case 'submit':
          response = await axios.post(
            `${API}/permits/${permit.id}/submit-for-approval`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'approve':
          response = await axios.post(
            `${API}/permits/${permit.id}/approve`,
            { approval_status: 'Approved', comments: payload.comments || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;

        // PTW-APPROVER-LOCATION (disabled): replace the 'approve' case above with this.
        // case 'approve': {
        //   // Record the device-reported location with the approval. Capture
        //   // always resolves — a denied/failed lookup is sent as its status and
        //   // the approval goes ahead regardless.
        //   setCapturingLocation(true);
        //   let approverLocation;
        //   try {
        //     approverLocation = await captureLocation();
        //   } finally {
        //     setCapturingLocation(false);
        //   }
        //   response = await axios.post(
        //     `${API}/permits/${permit.id}/approve`,
        //     {
        //       approval_status: 'Approved',
        //       comments: payload.comments || '',
        //       site_acknowledged: !!payload.site_acknowledged,
        //       approver_location: approverLocation,
        //     },
        //     { headers: { Authorization: `Bearer ${token}` } }
        //   );
        //   break;
        // }
          
        case 'reject':
          response = await axios.post(
            `${API}/permits/${permit.id}/approve`,
            { approval_status: 'Rejected', comments: payload.comments || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;

        case 'sendback':
          response = await axios.post(
            `${API}/permits/${permit.id}/return-for-correction`,
            { comments: payload.comments || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'issue':
          response = await axios.post(
            `${API}/permits/${permit.id}/issue`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'close':
          response = await axios.post(
            `${API}/permits/${permit.id}/close`,
            {
              closure_remarks: payload.remarks || '',
              file_ids: (payload.closeFiles || []).map(f => f.id)
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'complete-observation':
          response = await axios.post(
            `${API}/permits/${permit.id}/complete-observation`,
            {
              inspection: {
                no_fire_smoke_hotspots: !!payload.no_fire_smoke_hotspots,
                housekeeping_done: !!payload.housekeeping_done,
                fire_equipment_restored: !!payload.fire_equipment_restored,
                area_safe: !!payload.area_safe,
              },
              remarks: payload.remarks || '',
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;

        case 'suspend':
          response = await axios.post(
            `${API}/permits/${permit.id}/suspend`,
            { reason: payload.reason || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'extend':
          response = await axios.post(
            `${API}/permits/${permit.id}/request-extension`,
            {
              extension_end_date: new Date(payload.extension_end_date).toISOString(),
              extension_reason: payload.extension_reason
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'approve-extension':
          response = await axios.post(
            `${API}/permits/${permit.id}/approve-extension`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'hse-verify':
          response = await axios.post(
            `${API}/permits/${permit.id}/hse-verify`,
            { hse_verification_remarks: payload.remarks || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;

        case 'reissue':
          response = await axios.post(
            `${API}/permits/${permit.id}/reissue`,
            { reissue_remarks: payload.remarks || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;

        case 'cancel':
          response = await axios.post(
            `${API}/permits/${permit.id}/cancel`,
            { cancellation_reason: payload.reason || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
      }
      
      setShowModal(null);
      setModalData({});
      setLoading(false);
      
      if (onUpdate) {
        onUpdate();
      }

      showSuccess(response.data.message || 'Action completed successfully');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (detail?.[0]?.msg || err.message || 'Action failed');

      // `error` is rendered inside the modal — but actions invoked straight from a
      // button (Issue & Activate is one) have no modal, so the message was being
      // stored and then never shown: the click looked like it did nothing while
      // the network tab held a 409. The toast surfaces it either way.
      setError(message);
      showError(message);
      setLoading(false);

      // Some rejections change the record on the way out: issuing a permit whose
      // validity has elapsed marks it Expired before returning 409. Without a
      // refresh the screen would keep offering Issue & Activate on a permit the
      // server has already expired.
      if (err.response?.status === 409 && onUpdate) {
        onUpdate();
      }
    }
  };

  const renderModal = () => {
    if (!showModal) return null;

    const modalConfigs = {
      approve: {
        title: 'Approve Permit',
        icon: <CheckCircle className="w-6 h-6 text-green-600" />,
        color: 'green',
        fields: [
          // PTW-APPROVER-LOCATION (disabled):
          // { name: 'site_acknowledged', label: 'I confirm that I am approving this Permit to Work from the work site/location.', type: 'checkbox', required: true },
          { name: 'comments', label: 'Approval Comments', type: 'textarea', placeholder: 'Add approval comments (optional)' }
        ]
      },
      reject: {
        title: 'Reject Permit',
        icon: <XCircle className="w-6 h-6 text-red-600" />,
        color: 'red',
        fields: [
          { name: 'comments', label: 'Rejection Reason', type: 'textarea', placeholder: 'Reason for rejection (required)', required: true }
        ]
      },
      sendback: {
        title: 'Send Back for Correction',
        icon: <RefreshCw className="w-6 h-6 text-orange-600" />,
        color: 'orange',
        fields: [
          { name: 'comments', label: 'Correction Notes', type: 'textarea', placeholder: 'What needs to be corrected? (required)', required: true }
        ]
      },
      close: {
        title: 'Close Permit',
        icon: <FileCheck className="w-6 h-6 text-purple-600" />,
        color: 'purple',
        fields: [
          { name: 'remarks', label: 'Closure Remarks', type: 'textarea', placeholder: 'Work completion remarks' }
        ]
      },
      'complete-observation': {
        title: 'Physical Inspection & Close',
        icon: <FileCheck className="w-6 h-6 text-amber-600" />,
        color: 'orange',
        fields: [
          { name: 'no_fire_smoke_hotspots', label: 'No fire, smoke, or hot spots present', type: 'checkbox', required: true },
          { name: 'housekeeping_done', label: 'Housekeeping completed', type: 'checkbox', required: true },
          { name: 'fire_equipment_restored', label: 'Fire protection equipment restored to normal', type: 'checkbox', required: true },
          { name: 'area_safe', label: 'Area is safe for normal operations', type: 'checkbox', required: true },
          { name: 'remarks', label: 'Inspection Remarks', type: 'textarea', placeholder: 'Optional remarks' },
        ]
      },
      suspend: {
        // One action, two situations: stopping live work, or deferring a permit
        // that has not been issued yet. The wording covers both.
        title: permit.status === 'Approved' ? 'Put Permit on Hold' : 'Suspend Permit',
        icon: <PauseCircle className="w-6 h-6 text-amber-600" />,
        color: 'red',
        fields: [
          { name: 'reason',
            label: permit.status === 'Approved' ? 'Reason for Hold' : 'Suspension Reason',
            type: 'textarea',
            placeholder: permit.status === 'Approved'
              ? 'Why is this permit being deferred? (required)'
              : 'Emergency suspension reason (required)',
            required: true }
        ]
      },
      extend: {
        title: 'Request Extension',
        icon: <PlayCircle className="w-6 h-6 text-orange-600" />,
        color: 'orange',
        fields: [
          { name: 'extension_end_date', label: 'New End Date', type: 'datetime-local', required: true,
            min: permit.work_end_date ? (() => { const d = new Date(permit.work_end_date); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })() : undefined },
          { name: 'extension_reason', label: 'Extension Reason', type: 'textarea', placeholder: 'Reason for extension (required)', required: true }
        ]
      },
      'hse-verify': {
        title: 'HSE Verification',
        icon: <CheckCircle className="w-6 h-6 text-green-600" />,
        color: 'green',
        fields: [
          { name: 'remarks', label: 'Verification Remarks', type: 'textarea', placeholder: 'HSE verification remarks' }
        ]
      },
      reissue: {
        title: permit.permit_issued_date ? 'Reissue Permit' : 'Release Hold',
        icon: <RefreshCw className="w-6 h-6 text-green-600" />,
        color: 'green',
        // A permit held before it was ever issued goes back to Approved and must
        // still pass Issue & Activate — releasing a hold is not the same as
        // handing over a live permit, and the copy says which one this is.
        note: permit.permit_issued_date
          ? 'The permit returns to Active and work may resume.'
          : 'The permit returns to Approved. It must still be issued before work starts.',
        fields: [
          { name: 'remarks', label: 'Remarks', type: 'textarea', placeholder: 'Remarks (e.g., safety measures verified)' }
        ]
      },
      cancel: {
        title: 'Cancel Permit',
        icon: <Ban className="w-6 h-6 text-red-600" />,
        color: 'red',
        fields: [
          { name: 'reason', label: 'Cancellation Reason', type: 'textarea', placeholder: 'Reason for permanent cancellation (required)', required: true }
        ]
      }
    };

    const config = modalConfigs[showModal];
    if (!config) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              {config.icon}
              <h3 className="text-xl font-bold text-gray-900">{config.title}</h3>
            </div>

            {config.note && (
              <p className="mb-4 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700">
                {config.note}
              </p>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              {config.fields.map((field) => (
                <div key={field.name}>
                  {field.type !== 'checkbox' && (
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                  )}
                  {field.type === 'checkbox' ? (
                    <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!modalData[field.name]}
                        onChange={(e) => setModalData({ ...modalData, [field.name]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                    </label>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      value={modalData[field.name] || ''}
                      onChange={(e) => setModalData({ ...modalData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      rows="4"
                      placeholder={field.placeholder}
                      required={field.required}
                    />
                  ) : (
                    <>
                      <input
                        type={field.type}
                        value={modalData[field.name] || ''}
                        onChange={(e) => setModalData({ ...modalData, [field.name]: e.target.value })}
                        min={field.min || undefined}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                      {showModal === 'extend' && field.name === 'extension_end_date' && modalData.extension_end_date && permit.work_end_date &&
                        new Date(modalData.extension_end_date) <= new Date(permit.work_end_date) && (
                        <p className="text-red-500 text-sm mt-1">New End Date/Time must be after the current End Date/Time</p>
                      )}
                    </>
                  )}
                </div>
              ))}
              {showModal === 'close' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Attach Documents (optional)
                  </label>
                  <FileUploadWidget
                    module="ptw"
                    referenceId={permit.id}
                    maxFiles={5}
                    maxSizeMB={10}
                    allowedExtensions={['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png']}
                    existingFiles={closeFiles}
                    onFilesChange={setCloseFiles}
                    disabled={loading}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowModal(null);
                  setModalData({});
                  setError(null);
                  setCloseFiles([]);
                }}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleAction(showModal, { ...modalData, closeFiles })}
                className={`flex-1 bg-${config.color}-600 hover:bg-${config.color}-700`}
                disabled={loading
                  || (showModal === 'extend' && permit.work_end_date && (!modalData.extension_end_date || new Date(modalData.extension_end_date) <= new Date(permit.work_end_date)))
                  || (showModal === 'complete-observation' && !(modalData.no_fire_smoke_hotspots && modalData.housekeeping_done && modalData.fire_equipment_restored && modalData.area_safe))
                  // PTW-APPROVER-LOCATION (disabled):
                  // || (showModal === 'approve' && !modalData.site_acknowledged)
                }
              >
                {/* PTW-APPROVER-LOCATION (disabled): restore the capturing-location label:
                {capturingLocation
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Capturing location…</>
                  : loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'} */}
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActions = () => {
    const actions = [];
    const status = permit.status;
    // ESI/WC/insurance marked "No" blocks approval — coverage must be confirmed.
    const insuranceNotCovered = permit.esi_wc_covered === false;
    // A closed validity window blocks every step towards issuing — submit,
    // approve, issue. The server enforces each of these; this only keeps the user
    // from walking into the refusal. Reject and Send Back stay available so the
    // dates can still be corrected.
    const validityElapsed = Boolean(permit.work_end_date)
      && new Date(permit.work_end_date) < new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserId = user.user_id || user.id;
    // BUG-006: the creator may submit their own draft even without ptw:update.
    const isCreator = currentUserId && String(permit.created_by) === String(currentUserId);

    // Draft status — creator can submit their own draft; others need ptw:update
    if (status === 'Draft' && (isCreator || hasPermission('ptw', 'update'))) {
      if (validityElapsed) {
        actions.push(
          <div key="draft-validity-warn" className="w-full text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
            ⚠ The work end date on this permit has already passed. Edit the permit
            and set current validity dates before submitting it for approval.
          </div>
        );
      }
      actions.push(
        <Button
          key="submit"
          onClick={() => handleAction('submit')}
          disabled={loading || validityElapsed}
          title={validityElapsed ? 'Cannot submit — the work end date has already passed. Update the validity dates first.' : undefined}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Submit for Approval
        </Button>
      );
    }

    // Pending Approval status - Check if user has approve permission or is assigned approver
    if (status === 'Pending Approval' && hasPermission('ptw', 'approve')) {
      const currentApprovalLevel = permit.current_approval_level || 0;
      const approvals = permit.approvals || [];
      const currentApprover = approvals[currentApprovalLevel];
      const userId = user.user_id || user.id;

      const isAssignedApprover = currentApprover && currentApprover.approver_id === userId;

      if (isAssignedApprover || hasPermission('ptw', 'approve')) {
        if (insuranceNotCovered) {
          actions.push(
            <div key="ins-warn" className="w-full text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
              ⚠ Approval is blocked: Insurance Coverage (ESI / WC / Other Policy) is marked <strong>No</strong> on this permit. Coverage must be confirmed before it can be approved.
            </div>
          );
        }
        if (validityElapsed) {
          actions.push(
            <div key="approve-validity-warn" className="w-full text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
              ⚠ Approval is blocked: this permit's <strong>validity period has already elapsed</strong>,
              so it could never be issued. Send it back for correction so the work
              dates can be updated, or reject it.
            </div>
          );
        }
        actions.push(
          <Button
            key="approve"
            onClick={() => setShowModal('approve')}
            disabled={loading || insuranceNotCovered || validityElapsed}
            title={
              insuranceNotCovered ? 'Cannot approve — insurance coverage is marked No'
                : validityElapsed ? "Cannot approve — this permit's validity period has already elapsed, so it could never be issued. Send it back for correction instead."
                  : undefined
            }
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve
          </Button>,
          <Button
            key="reject"
            onClick={() => setShowModal('reject')}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Reject
          </Button>,
          <Button
            key="sendback"
            onClick={() => setShowModal('sendback')}
            disabled={loading}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Send Back for Correction
          </Button>
        );
      }
    }

    // Approved status
    if (status === 'Approved' && hasPermission('ptw', 'approve')) {
      // A permit can sit at Approved long enough for its own validity window to
      // pass — most easily by being put on hold and released later. Issuing it is
      // then refused server-side (and the permit is marked Expired), and it cannot
      // be extended either, because extensions need an Active permit. Offering the
      // button would walk the user into that; the only way out is to cancel and
      // raise a new permit, which is the action left enabled below.
      //
      // Submit and approve are gated on the same validityElapsed flag now, so a
      // permit reaches this state only if the window closed AFTER it was
      // approved — most often via a hold that was released later.

      actions.push(
        validityElapsed ? (
          <Button
            key="issue"
            disabled
            className="bg-green-600"
            title="This permit's validity period has already elapsed, so it can no longer be issued. Cancel it and raise a new permit with current dates."
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Issue & Activate
          </Button>
        ) : (
          <Button
            key="issue"
            onClick={() => handleAction('issue')}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            Issue & Activate
          </Button>
        ),
        // Issuing was previously the only way forward from here. A job called off
        // between approval and issue — weather, a shutdown, a contractor who did
        // not arrive — had to be issued and then suspended and cancelled, which
        // recorded an activation that never happened.
        <Button
          key="hold"
          onClick={() => setShowModal('suspend')}
          disabled={loading}
          variant="outline"
          className="border-amber-500 text-amber-700 hover:bg-amber-50"
          title="Defer this permit without issuing it. It can be released back to Approved later."
        >
          <PauseCircle className="w-4 h-4 mr-2" />
          Put on Hold
        </Button>,
        <Button
          key="cancel"
          onClick={() => setShowModal('cancel')}
          disabled={loading}
          variant="outline"
          className="border-red-500 text-red-700 hover:bg-red-50"
          title="Retire this permit without issuing it. This cannot be undone."
        >
          <Ban className="w-4 h-4 mr-2" />
          Cancel Permit
        </Button>
      );
    }

    // Active status
    if (status === 'Active') {
      if (hasPermission('ptw', 'update')) {
        actions.push(
          <Button
            key="extend"
            onClick={() => setShowModal('extend')}
            disabled={loading}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Request Extension
          </Button>
        );
      }
      const currentUserId = user?.id || user?.user_id;
      const isInitiator = currentUserId && String(currentUserId) === String(permit.applicant_id);
      const isHotWork = (permit.permit_types || []).includes('Hot Work') || permit.permit_type === 'Hot Work';
      if (hasPermission('ptw', 'close') && isInitiator) {
        actions.push(
          <Button
            key="close"
            onClick={() => setShowModal('close')}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
            title={isHotWork ? 'Completes work and starts the 30-minute fire-watch observation' : 'Close the permit'}
          >
            <FileCheck className="w-4 h-4 mr-2" />
            {isHotWork ? 'Complete Work' : 'Close Permit'}
          </Button>
        );
      }
      if (hasPermission('ptw', 'approve')) {
        actions.push(
          <Button
            key="suspend"
            onClick={() => setShowModal('suspend')}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            <StopCircle className="w-4 h-4 mr-2" />
            Suspend
          </Button>
        );
      }
    }

    // Under Observation (Hot Work) — initiator closes after the 30-min fire watch
    // and a physical inspection.
    if (status === 'Under Observation') {
      const currentUserId = user?.id || user?.user_id;
      const isInitiator = currentUserId && String(currentUserId) === String(permit.applicant_id);
      if (isInitiator || hasPermission('ptw', 'close')) {
        const requiredMin = permit.observation_minutes_required || 30;
        const startedAt = permit.observation_started_at ? new Date(permit.observation_started_at) : null;
        const elapsedMin = startedAt ? (Date.now() - startedAt.getTime()) / 60000 : 999;
        const remaining = Math.max(0, Math.ceil(requiredMin - elapsedMin));
        actions.push(
          <div key="obs" className="flex flex-col gap-1">
            <Button
              onClick={() => setShowModal('complete-observation')}
              disabled={loading || remaining > 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <FileCheck className="w-4 h-4 mr-2" />
              Physical Inspection &amp; Close
            </Button>
            <span className="text-xs text-gray-500">
              {remaining > 0
                ? `Fire-watch observation in progress — inspection available in ~${remaining} min (refresh to update)`
                : 'Observation complete — ready for the physical inspection'}
            </span>
          </div>
        );
      }
    }

    // Extension Requested status
    if (status === 'Extension Requested' && hasPermission('ptw', 'approve')) {
      actions.push(
        <Button
          key="approve-extension"
          onClick={() => handleAction('approve-extension')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Approve Extension
        </Button>
      );
    }

    // Closed status — HSE Verify restricted to HSE Manager role
    if (status === 'Closed' && (hasRole('HSE Manager') || hasRole('hse_manager'))) {
      actions.push(
        <Button
          key="hse-verify"
          onClick={() => setShowModal('hse-verify')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          HSE Verify
        </Button>
      );
    }

    // Suspended status - show reissue and cancel options
    if (status === 'Suspended' && hasPermission('ptw', 'approve')) {
      actions.push(
        <Button
          key="reissue"
          onClick={() => setShowModal('reissue')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {permit.permit_issued_date ? 'Reissue Permit' : 'Release Hold'}
        </Button>,
        <Button
          key="cancel"
          onClick={() => setShowModal('cancel')}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700"
        >
          <Ban className="w-4 h-4 mr-2" />
          Cancel Permit
        </Button>
      );
    }

    return actions;
  };

  const validityElapsed = permit.work_end_date
    && new Date(permit.work_end_date) < new Date();
  const stuckAtApproved = permit.status === 'Approved' && validityElapsed;

  return (
    <>
      {stuckAtApproved && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>
            <strong>Validity period has elapsed.</strong> This permit can no longer
            be issued, and an extension cannot be requested before it is active.
            Cancel it and raise a new permit with current dates.
          </AlertDescription>
        </Alert>
      )}

      {/* A failure from an action with no modal has nowhere else to appear. */}
      {error && !showModal && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap gap-3">
        {renderActions()}
      </div>
      {renderModal()}
    </>
  );
};

export default PTWWorkflowActions;
